import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  locationsApi,
  mediaApi,
  type Location as LocationType,
  type LocationInput,
} from '../api';
import { descendantsOf, flattenTree } from '../lib/tree';
import { canWrite } from '../lib/permissions';
import { useAuth } from '../context/AuthContext';
import SingleImageUpload from '../components/SingleImageUpload';

export default function LocationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const writeAccess = canWrite(user?.role);
  const [editing, setEditing] = useState<LocationType | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.list,
  });

  const tree = flattenTree(locations);

  const removeMutation = useMutation({
    mutationFn: (id: number) => locationsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  });

  function startEdit(loc: LocationType) {
    setEditing(loc);
    setShowForm(true);
  }

  function startCreate() {
    setEditing(null);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('locations.title')}</h1>
          <p className="text-sm text-slate-600">{t('locations.intro')}</p>
        </div>
        {writeAccess && !showForm && (
          <button
            onClick={startCreate}
            className="bg-slate-900 text-white rounded px-4 py-2 hover:bg-slate-800 text-sm font-medium"
          >
            {t('locations.new')}
          </button>
        )}
      </div>

      {showForm && (
        <LocationForm locations={locations} editing={editing} onClose={closeForm} />
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-500">{t('common.loading')}</div>
        ) : tree.length === 0 ? (
          <div className="p-8 text-center text-slate-500">{t('locations.empty')}</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">
                  {t('field.name')}
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">
                  {t('field.path')}
                </th>
                {writeAccess && (
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase">
                    {t('common.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {tree.map((loc) => (
                <tr key={loc.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2">
                    <div style={{ paddingLeft: `${loc.depth * 24}px` }}>
                      <span className="font-medium text-slate-900">{loc.name}</span>
                      {loc.description && (
                        <span className="text-sm text-slate-500"> — {loc.description}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm text-slate-600">{loc.path}</td>
                  {writeAccess && (
                    <td className="px-4 py-2 text-right text-sm space-x-3">
                      <button
                        onClick={() => startEdit(loc)}
                        className="text-slate-600 hover:text-slate-900 hover:underline"
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(t('locations.confirmDelete', { name: loc.name }))) {
                            removeMutation.mutate(loc.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 hover:underline"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function LocationForm({
  locations,
  editing,
  onClose,
}: {
  locations: LocationType[];
  editing: LocationType | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [parentId, setParentId] = useState<number | null>(editing?.parentId ?? null);
  const [stagedImage, setStagedImage] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (data: LocationInput) => {
      const result = editing
        ? await locationsApi.update(editing.id, data)
        : await locationsApi.create(data);
      if (stagedImage && !editing) {
        try {
          await mediaApi.upload('locations', result.id, stagedImage, 'photo');
        } catch (err) {
          console.error('Failed to upload staged image:', err);
        }
      }
      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations'] });
      qc.invalidateQueries({ queryKey: ['media'] });
      qc.invalidateQueries({ queryKey: ['storage-usage'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? t([`error.${err.message}`, 'error.UNKNOWN']) : t('error.UNKNOWN'));
    },
  });

  const blocked = editing ? descendantsOf(locations, editing.id) : new Set<number>();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    saveMutation.mutate({
      name,
      description: description || null,
      parentId,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">
        {editing ? t('locations.editTitle') : t('locations.newTitle')}
      </h2>

      {error && <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

      <label className="block">
        <span className="text-sm font-medium text-slate-700">{t('field.name')}</span>
        <input
          type="text"
          required
          maxLength={200}
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-slate-700">{t('field.description')}</span>
        <textarea
          rows={2}
          maxLength={2000}
          value={description ?? ''}
          onChange={(e) => setDescription(e.target.value)}
          className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        />
      </label>

      <div>
        <span className="text-sm font-medium text-slate-700">{t('field.image')}</span>
        <div className="mt-1">
          {editing ? (
            <SingleImageUpload target="locations" targetId={editing.id} />
          ) : (
            <SingleImageUpload
              target="locations"
              stagedBlob={stagedImage}
              onStaged={setStagedImage}
            />
          )}
        </div>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-slate-700">{t('field.parent')}</span>
        <select
          value={parentId ?? ''}
          onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : null)}
          className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        >
          <option value="">{t('field.none')}</option>
          {flattenTree(locations)
            .filter((l) => !blocked.has(l.id))
            .map((l) => (
              <option key={l.id} value={l.id}>
                {'— '.repeat(l.depth)}
                {l.name}
              </option>
            ))}
        </select>
      </label>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded text-sm"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="bg-slate-900 text-white rounded px-4 py-2 hover:bg-slate-800 text-sm font-medium disabled:opacity-50"
        >
          {saveMutation.isPending ? t('common.working') : t('common.save')}
        </button>
      </div>
    </form>
  );
}
